import React, { useState, useEffect } from 'react';
import { Shield, FileText, Search, Plus, Archive, ArchiveRestore, Upload, AlertTriangle, Eye, Download, Trash2, Loader2, RefreshCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Asset } from '../../../types/asset';
import AssetDocumentModal from './AssetDocumentModal';
import api from '../../../utils/api';
import { toast } from 'sonner';
import { toSydneyDate } from '../../../utils/helpers';

interface AssetDocumentsProps {
    asset: Asset;
}

const AssetDocuments: React.FC<AssetDocumentsProps> = ({ asset }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [documents, setDocuments] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);
    const [showArchived, setShowArchived] = useState(false);

    useEffect(() => {
        const fetchDocuments = async () => {
            setIsLoading(true);
            try {
                const data = await api.get(`/api/assets/${asset.id}/documents?archived=${showArchived}`);
                setDocuments(data);
            } catch (error) {
                console.error("Failed to fetch documents", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchDocuments();
    }, [asset.id, isModalOpen, showArchived]);

    const handleToggleArchive = async (documentId: number, currentStatus: boolean) => {
        try {
            const action = currentStatus ? 'restore' : 'archive';
            await api.post(`/api/assets/${asset.id}/documents/${documentId}/${action}`, {});
            toast.success(`Document ${currentStatus ? 'restored' : 'archived'} successfully`);
            setDocuments(documents.filter(d => d.id !== documentId));
        } catch (error: any) {
            toast.error(error.message || `Failed to ${currentStatus ? 'restore' : 'archive'} document`);
        }
    };

    const confirmDelete = async () => {
        if (documentToDelete === null) return;
        try {
            await api.delete(`/api/assets/${asset.id}/documents/${documentToDelete}`);
            setDocuments(documents.filter(d => d.id !== documentToDelete));
            toast.success("Document deleted successfully");
        } catch (error: any) {
            toast.error(error.message || "Failed to delete document");
        } finally {
            setDocumentToDelete(null);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Vehicle Renewals Section - Only for Vehicle type */}
            {asset.type === 'Vehicle' && (
                <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                    <div className="flex items-center gap-2">
                        <Shield className="text-orange-500" size={24} />
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Vehicle Renewals</h3>
                            <p className="text-gray-500 text-sm">Registration, insurance, and greenslip status</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Registration Papers */}
                        <Card className="rounded-[24px] border-gray-100 shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-gray-50 mb-2">
                                <h4 className="font-bold text-gray-900 text-sm">Registration Papers</h4>
                            </div>
                            <CardContent className="p-6 flex flex-col items-center justify-center text-center space-y-4">
                                <div>
                                    <AlertTriangle className="text-orange-500 mx-auto mb-2" size={32} />
                                    <p className="text-sm font-medium text-gray-400">No registration papers uploaded</p>
                                </div>
                                <Button variant="outline" className="rounded-xl border-gray-200 text-gray-700 bg-white hover:bg-gray-50 gap-2 px-4 md:px-6" onClick={() => setIsModalOpen(true)}>
                                    <Upload size={16} />
                                    <span className="hidden md:inline">Upload</span>
                                </Button>
                            </CardContent>
                        </Card>

                        {/* CTP / Greenslip */}
                        <Card className="rounded-[24px] border-gray-100 shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-gray-50 mb-2">
                                <h4 className="font-bold text-gray-900 text-sm">CTP / Greenslip</h4>
                            </div>
                            <CardContent className="p-6 flex flex-col items-center justify-center text-center space-y-4">
                                <div>
                                    <AlertTriangle className="text-orange-500 mx-auto mb-2" size={32} />
                                    <p className="text-sm font-medium text-gray-400">No ctp / greenslip uploaded</p>
                                </div>
                                <Button variant="outline" className="rounded-xl border-gray-200 text-gray-700 bg-white hover:bg-gray-50 gap-2 px-4 md:px-6" onClick={() => setIsModalOpen(true)}>
                                    <Upload size={16} />
                                    <span className="hidden md:inline">Upload</span>
                                </Button>
                            </CardContent>
                        </Card>

                        {/* Comprehensive Insurance */}
                        <Card className="rounded-[24px] border-gray-100 shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-gray-50 mb-2">
                                <h4 className="font-bold text-gray-900 text-sm">Comprehensive Insurance</h4>
                            </div>
                            <CardContent className="p-6 flex flex-col items-center justify-center text-center space-y-4">
                                <div>
                                    <AlertTriangle className="text-orange-500 mx-auto mb-2" size={32} />
                                    <p className="text-sm font-medium text-gray-400">No comprehensive insurance uploaded</p>
                                </div>
                                <Button variant="outline" className="rounded-xl border-gray-200 text-gray-700 bg-white hover:bg-gray-50 gap-2 px-4 md:px-6" onClick={() => setIsModalOpen(true)}>
                                    <Upload size={16} />
                                    <span className="hidden md:inline">Upload</span>
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </section>
            )}

            {/* All Documents Section */}
            <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">All Documents</h3>
                        <p className="text-gray-500 text-sm">{documents.length} {showArchived ? 'archived' : 'active'} documents</p>
                    </div>
                    <Button className="rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 gap-2 px-4 md:px-6" onClick={() => setIsModalOpen(true)}>
                        <Plus size={16} />
                        <span className="hidden md:inline">Upload Document</span>
                    </Button>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <Input
                            placeholder="Search documents..."
                            className="pl-10 h-11 bg-gray-50 border-transparent rounded-xl focus-visible:ring-1 focus-visible:ring-gray-200"
                        />
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <select className="h-11 px-3 border border-gray-200 bg-white rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-gray-200 w-full sm:w-40">
                            <option>All Categories</option>
                            <option>Purchase & Ownership</option>
                            <option>Warranty</option>
                            <option>Service Agreements</option>
                            <option>Vehicle Documents</option>
                            <option>Compliance & Certificates</option>
                        </select>
                        <Button
                            variant={showArchived ? "default" : "outline"}
                            className={`h-11 rounded-xl font-bold gap-2 shrink-0 ${showArchived ? "bg-slate-900 text-white hover:bg-slate-800" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
                            onClick={() => setShowArchived(!showArchived)}
                        >
                            <Archive size={16} />
                            {showArchived ? 'Active' : 'Archived'}
                        </Button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="py-16 flex justify-center items-center">
                        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                    </div>
                ) : documents.length > 0 ? (
                    <div className="border border-gray-100 rounded-2xl overflow-hidden mt-6 shadow-sm">
                        <Table>
                            <TableHeader className="bg-gray-50/80">
                                <TableRow className="border-b border-gray-100/50 hover:bg-transparent">
                                    <TableHead className="font-bold text-gray-900 py-4 h-auto text-xs uppercase tracking-wider">Document Name</TableHead>
                                    <TableHead className="font-bold text-gray-900 py-4 h-auto text-xs uppercase tracking-wider">Type</TableHead>
                                    <TableHead className="font-bold text-gray-900 py-4 h-auto text-xs uppercase tracking-wider">Issue Date</TableHead>
                                    <TableHead className="font-bold text-gray-900 py-4 h-auto text-xs uppercase tracking-wider">Expiry Date</TableHead>
                                    <TableHead className="font-bold text-gray-900 p-4 h-auto text-right text-xs uppercase tracking-wider w-[120px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {documents.map((doc) => (
                                    <TableRow key={doc.id} className="border-b border-gray-50/50 hover:bg-gray-50/50 transition-colors">
                                        <TableCell className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100/50 flex items-center justify-center shrink-0">
                                                    <FileText size={18} className="text-orange-500" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 text-sm">{doc.title}</p>
                                                    {doc.notes && <p className="text-xs text-gray-400 mt-0.5 font-medium truncate max-w-[200px]">{doc.notes}</p>}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="p-4">
                                            <span className="px-2.5 py-1 rounded-lg bg-gray-50 text-gray-700 text-xs font-bold border border-gray-200 shadow-sm whitespace-nowrap">
                                                {doc.type}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-gray-600 font-medium text-sm p-4">
                                            {toSydneyDate(doc.issueDate) || '-'}
                                        </TableCell>
                                        <TableCell className="text-gray-600 font-medium text-sm p-4">
                                            {toSydneyDate(doc.expiryDate) || '-'}
                                        </TableCell>
                                        <TableCell className="p-4 text-right align-middle">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                                    onClick={() => window.open(doc.url, '_blank')}
                                                    title="Preview Document"
                                                >
                                                    <Eye size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                                    onClick={() => window.open(doc.url, '_blank')}
                                                    title="Download Document"
                                                >
                                                    <Download size={16} />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    onClick={() => handleToggleArchive(doc.id, showArchived)}
                                                    title={showArchived ? "Restore Document" : "Archive Document"}
                                                >
                                                    {showArchived ? <RefreshCcw size={16} /> : <Archive size={16} />}
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    onClick={() => setDocumentToDelete(doc.id)}
                                                    title="Delete Document"
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                ) : (
                    <div className="py-16 flex flex-col items-center justify-center text-center space-y-4">
                        <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mb-2">
                            <FileText className="text-gray-400" size={32} />
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900 text-lg">No documents found</h4>
                            <p className="text-gray-500 text-sm mt-1">Upload your first document to get started</p>
                        </div>
                        <Button className="rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 gap-2 mt-2 px-4 md:px-6" onClick={() => setIsModalOpen(true)}>
                            <Plus size={16} />
                            <span className="hidden md:inline">Upload Document</span>
                        </Button>
                    </div>
                )}
            </section>

            <AssetDocumentModal open={isModalOpen} onClose={() => setIsModalOpen(false)} asset={asset} />

            <AlertDialog open={documentToDelete !== null} onOpenChange={(open) => !open && setDocumentToDelete(null)}>
                <AlertDialogContent className="max-w-md rounded-2xl border-0 shadow-2xl bg-white p-6">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold flex items-center gap-2">
                            <AlertTriangle className="text-red-500" size={24} />
                            Delete Document
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-base text-gray-500 font-medium mt-3">
                            Are you sure you want to delete <strong className="text-gray-900">{documents.find(d => d.id === documentToDelete)?.title} - {documents.find(d => d.id === documentToDelete)?.type}</strong>? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-6">
                        <AlertDialogCancel className="rounded-xl border-gray-200 font-bold h-11 px-6">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold h-11 px-6 border-0"
                        >
                            Delete Document
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AssetDocuments;
